import React from "react";
import { UserProvider, UserProviderProps } from "@auth0/nextjs-auth0/client";
import { Environment } from "@carvana/experiment/SharedTypes";
import {
  LDProvider,
  LDContext,
  LDFlagSet,
} from "launchdarkly-react-client-sdk";
import { SkeletonTheme } from "react-loading-skeleton";

import { ApolloProvider } from "@apollo/client";
import { getApolloClient } from "@/apollo/apolloClient";
import { ToastProvider } from "@/contexts/ToastContext";
import { ExperimentProvider } from "@/contexts/ExperimentContext";
import { AnalyticsProvider } from "@/contexts/AnalyticsContext";
import { longEnv } from "@/utils/longEnv";
import {
  DivvyExp,
  ExperimentDetails,
} from "@/server-utils/getExperimentsServer";

import "react-loading-skeleton/dist/skeleton.css";

type Props = {
  auth0User?: UserProviderProps["user"] & { representativeId?: string };
  auth0ProfileUrl?: string;
  auth0LoginUrl?: string;
  basePath?: string;
  ld?: {
    features: LDFlagSet;
    context: LDContext;
  };
  experiments?: DivvyExp;
  children: React.ReactNode;
};

export default function AppProviders({
  auth0User,
  auth0ProfileUrl = "/api/auth/me",
  auth0LoginUrl = "/api/auth/login",
  basePath = "",
  ld,
  experiments,
  children,
}: Props) {
  if (!process.env.NEXT_PUBLIC_SUBSCRIPTIONS_URL) {
    throw new Error("NEXT_PUBLIC_SUBSCRIPTIONS_URL is not defined");
  }

  const client = getApolloClient({
    basePath,
    subscriptionUrl: process.env.NEXT_PUBLIC_SUBSCRIPTIONS_URL,
    enableSubscriptionLogging:
      ld?.features["auction-enable-subscription-logging"],
  });

  // onPageLoadExperiments is solely for local development where MFEs do not have access to server side shell functionality
  const onPageLoadExperiments: Record<string, ExperimentDetails> = {};
  const auctionEnabledExperiments = ld?.features["auction-enabled-experiments"];
  if (Array.isArray(auctionEnabledExperiments)) {
    auctionEnabledExperiments.forEach((exp) => {
      onPageLoadExperiments[exp] = {
        defaultBucket: "control",
        identifier: auth0User?.representativeId,
      };
    });
  }

  return (
    <UserProvider
      user={auth0User}
      loginUrl={auth0LoginUrl}
      profileUrl={auth0ProfileUrl}
    >
      <LDProvider
        clientSideID={process.env.NEXT_PUBLIC_LD_CLIENT_ID ?? "missing LD ID"}
        reactOptions={{
          useCamelCaseFlagKeys: true,
        }}
        context={ld?.context}
        options={{
          bootstrap: ld?.features || {},
        }}
      >
        <SkeletonTheme
          baseColor="#F4F8FA"
          borderRadius={0}
          highlightColor="#FCFCFC"
        >
          <ApolloProvider client={client}>
            <AnalyticsProvider
              writeKey={
                process.env.NEXT_PUBLIC_CARVANA_ACCESS_SEGMENT_KEY ?? ""
              }
            >
              <ExperimentProvider
                defaults={{
                  cacheMinutes: 30,
                  environment: longEnv(
                    process.env.NEXT_PUBLIC_DEPLOY_ENV,
                  ) as Environment,
                }}
                onPageLoadExperiments={onPageLoadExperiments}
                preloadedExperiments={experiments}
              >
                <ToastProvider>{children}</ToastProvider>
              </ExperimentProvider>
            </AnalyticsProvider>
          </ApolloProvider>
        </SkeletonTheme>
      </LDProvider>
    </UserProvider>
  );
}
